import { useEffect, useState } from "react";
import FilterTags from "./FilterTags";
import AdminContent from "./AdminContent";
import AdminVariables from "./AdminVariables";
import AdminOrganizations from "./AdminOrganizations";
import AdminRoutes from "./AdminRoutes";

const API = process.env.REACT_APP_BACKEND_URL;

function redirectToLogin() {
  window.location.href = "/login";
}

function apiFetch(url, csrfToken, options = {}) {
  return fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "CSRF-Token": csrfToken,
      ...options.headers
    },
    ...options
  }).then(r => {
    if (r.status === 401) redirectToLogin();
    return r;
  });
}

function AthleteRow({ athlete, csrfToken, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [form, setForm] = useState({
    nickname: athlete.nickname || "",
    eventFilters: (athlete.eventFilters || []), //.join(", "),
    trainingFilters: (athlete.trainingFilters || []), //.join(", ")
  });

  const sync = async () => {
    setSyncing(true);
    setSyncError("");
    const res = await apiFetch(`${API}/athletes/${athlete._id}/sync`, csrfToken, { method: "POST" });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      setSyncError(data?.error || "Synchroniseren mislukt");
    }
    setSyncing(false);
  };

  const approve = async () => {
    await apiFetch(`${API}/athletes/${athlete._id}/approve`, csrfToken, { method: "POST" });
    onUpdate(athlete._id, { approved: true });
  };

  const revokeApproval = async () => {
    await apiFetch(`${API}/athletes/${athlete._id}`, csrfToken, {
      method: "PUT",
      body: JSON.stringify({ approved: false })
    });
    onUpdate(athlete._id, { approved: false });
  };

  const saveEdit = async () => {
    const patch = {
      nickname: form.nickname,
      eventFilters: form.eventFilters./*split(",").*/map(s => s.trim()).filter(Boolean),
      trainingFilters: form.trainingFilters./*split(",").*/map(s => s.trim()).filter(Boolean)
    };
    await apiFetch(`${API}/athletes/${athlete._id}`, csrfToken, {
      method: "PUT",
      body: JSON.stringify(patch)
    });
    onUpdate(athlete._id, patch);
    setEditing(false);
  };

  return (
    <div className="admin-athlete-row">
      <div className="admin-athlete-header">
        <span className="admin-athlete-name">
          {athlete.name}
          {athlete.nickname && <em> &ldquo;{athlete.nickname}&rdquo;</em>}
          {!athlete.approved && <span className="badge-pending"> — wacht op goedkeuring</span>}
        </span>
        <div className="admin-athlete-actions">
          {!athlete.approved ? (
            <button onClick={approve}>Goedkeuren</button>
          ) : (
            <button onClick={revokeApproval} className="btn-secondary">Goedkeuring intrekken</button>
          )}
          <button onClick={sync} disabled={syncing}>{syncing ? "Bezig…" : "Sync"}</button>
          <button onClick={() => setEditing(e => !e)} className="btn-secondary">
            {editing ? "Sluiten" : "Bewerken"}
          </button>
        </div>
      </div>

      {syncError && <p className="content-error">{syncError}</p>}

      {editing && (
        <div className="admin-edit-form">
          <label>Weergavenaam</label>
          <input
            value={form.nickname}
            onChange={e => setForm(f => ({ ...f, nickname: e.target.value }))}
            placeholder="Nickname"
          />
          <label>Evenement-filters</label>
          <FilterTags
            filters={form.eventFilters}
            onChange={newFilters => setForm(f => ({ ...f, eventFilters: newFilters }))}
            placeholder="bijv. Hartekind"
          />
          <label>Trainings-filters</label>
          <FilterTags
            filters={form.trainingFilters}
            onChange={newFilters => setForm(f => ({ ...f, trainingFilters: newFilters }))}
            placeholder="bijv. training"
          />
          <button onClick={saveEdit}>Opslaan</button>

        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [athletes, setAthletes] = useState([]);
  const [csrfToken, setCsrfToken] = useState(null);
  const [activeTab, setActiveTab] = useState("athletes");

  useEffect(() => {
    fetch(`${API}/athletes`, { credentials: "include" })
      .then(r => { if (r.status === 401) { redirectToLogin(); return null; } return r.json(); })
      .then(data => { if (Array.isArray(data)) setAthletes(data); });
  }, []);

  useEffect(() => {
    fetch(`${API}/api/csrf-token`, { credentials: "include" })
      .then(r => { if (r.status === 401) { redirectToLogin(); return null; } return r.json(); })
      .then(data => { if (data) setCsrfToken(data.csrfToken); });
  }, []);

  // Periodiek sessie controleren — redirect naar login als verlopen
  useEffect(() => {
    const check = () => {
      fetch(`${API}/me`, { credentials: "include" })
        .then(r => { if (r.status === 401) redirectToLogin(); });
    };
    const interval = setInterval(check, 4 * 60 * 1000); // elke 4 minuten
    return () => clearInterval(interval);
  }, []);

  const handleUpdate = (id, patch) => {
    setAthletes(prev => prev.map(a => a._id === id ? { ...a, ...patch } : a));
  };

  const pending = athletes.filter(a => !a.approved);
  const approved = athletes.filter(a => a.approved);

  return (
    <div className="pane pane-center">
      <h1>Admin</h1>

      <div className="admin-tabs">
        <button
          className={`admin-tab${activeTab === "athletes" ? " active" : ""}`}
          onClick={() => setActiveTab("athletes")}
        >
          Atleten {pending.length > 0 && <span className="badge-pending">({pending.length})</span>}
        </button>
        <button
          className={`admin-tab${activeTab === "content" ? " active" : ""}`}
          onClick={() => setActiveTab("content")}
        >
          Content
        </button>
        <button
          className={`admin-tab${activeTab === "variables" ? " active" : ""}`}
          onClick={() => setActiveTab("variables")}
        >
          Variabelen
        </button>
        <button
          className={`admin-tab${activeTab === "organizations" ? " active" : ""}`}
          onClick={() => setActiveTab("organizations")}
        >
          Organisatie
        </button>
        <button
          className={`admin-tab${activeTab === "routes" ? " active" : ""}`}
          onClick={() => setActiveTab("routes")}
        >
          Etappes
        </button>
      </div>

      {activeTab === "athletes" && (
        <>
          {pending.length > 0 && (
            <section>
              <h2>Wacht op goedkeuring ({pending.length})</h2>
              {pending.map(a => (
                <AthleteRow key={a._id} athlete={a} csrfToken={csrfToken} onUpdate={handleUpdate} />
              ))}
            </section>
          )}
          <section>
            <h2>Atleten ({approved.length})</h2>
            {approved.length === 0 && <p>Nog geen goedgekeurde atleten.</p>}
            {approved.map(a => (
              <AthleteRow key={a._id} athlete={a} csrfToken={csrfToken} onUpdate={handleUpdate} />
            ))}
          </section>
        </>
      )}

      {activeTab === "content" && (
        <AdminContent csrfToken={csrfToken} />
      )}

      {activeTab === "variables" && (
        <AdminVariables csrfToken={csrfToken} />
      )}

      {activeTab === "organizations" && (
        <AdminOrganizations csrfToken={csrfToken} />
      )}

      {activeTab === "routes" && (
        <AdminRoutes csrfToken={csrfToken} />
      )}
    </div>
  );
}
